import React from "react";
import { useRelayEnvironment } from "react-relay";
import { Link, useHistory } from "react-router-dom";
import { type ControlProps, components as reactSelectComponents } from "react-select";

import { faArrowRight, faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import cx from "classnames";
import { graphql } from "relay-runtime";

import { BillingIdentity } from "~/billing";
import { AppContext } from "~/common";
import { warning } from "~/lib/prompts";
import { commitMutationPromise, useLazyLoadQuery, useMutationPromise } from "~/lib/relayUtil";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { useCustomVocabularyFeatureFlagOn } from "~/meetingRecorder/featureFlag";
import ChannelSelector, { type Channel } from "~/recordings/ChannelSelector";
import AutoRecordingSettingsSelector, { type UIAutoRecordSetting } from "~/settings/AutoRecordingSettingsSelector";
import RecapSettings from "~/settings/RecapSettings";
import {
	type AttendeeValueOptions,
	mapUIAttendeeValueToUserMeetingRecapSendTo,
} from "~/settings/RecapSettingsSelector";
import { Button, toast, Tooltip } from "~/ui";
import { ButtonLink, HelpCenterLink, Input, Switch, TextChip } from "~/ui/legacyFellowIndex";
import Selector, { type SelectorOption } from "~/ui/selectors/Selector";

import { CustomRulesBanner, CustomRulesSettings } from "./CustomRules";
import {
	Divider,
	InfoBanner,
	Label,
	Row,
	SettingsPageSubtitle,
	SettingsPageTitle,
	SubLabel,
	useUserSetting,
} from "./shared";
import { useCustomVocabularySetting } from "./useCustomVocabularySetting";
import { VoicePrints } from "./VoicePrints";

import type { MeetingCopilotAutoPublishSettingsMutation } from "~/__generated__/MeetingCopilotAutoPublishSettingsMutation.graphql";
import type { MeetingCopilotAutoRecordSettingsMutation } from "~/__generated__/MeetingCopilotAutoRecordSettingsMutation.graphql";
import type { MeetingCopilotAutoShareSettingsMutation } from "~/__generated__/MeetingCopilotAutoShareSettingsMutation.graphql";
import type { MeetingCopilotSettingsQuery } from "~/__generated__/MeetingCopilotSettingsQuery.graphql";
import type { MeetingCopilotUpdateMeetingBriefSettingsMutation } from "~/__generated__/MeetingCopilotUpdateMeetingBriefSettingsMutation.graphql";

import settingsStyles from "~/settings/css/settings.module.css";

import styles from "./MeetingCopilot.module.css";

const query = graphql`
	query MeetingCopilotSettingsQuery {
		viewer {
			...BillingIdentityFeaturesPaywall_viewer
			id
			hasAccessToCopilot
			voicePrintCount
			...CustomRulesSettings
			...CustomRulesBanner
			user {
				id
				isAccountAdmin
			}
			account {
				id
				logo
				name
				allowMeetingRecording
				allowUserAutoRecord
				allowPublicRecap
				allowVoicePrints
				primaryAdmin {
					id
					fullName
					email
				}
				emailDomainList
				billingAccount {
					hasAccessToDefaultUserConfigurations: hasAccessToFeature(feature: default_user_configurations)
				}
				defaultUserAutoRecordSettings {
					enabled
					meetingOwnershipFilter
					allowUsersToChangeAutoRecordSettings
				}
				defaultUserMeetingRecapAutoShareSettings {
					sendTo
					alwaysEmailWhenAttendee
					allowUsersToChangeSendTo
					allowUsersToChangeAlwaysEmailWhenAttendee
				}
				customVocabularyList
				customVocabularyEnabled
			}
			autoRecordSettings {
				id
				enabled
				meetingOwnershipFilter
			}
			meetingRecapAutoShareSettings {
				id
				sendTo
				alwaysEmailWhenAttendee
			}
			autoPublishRecordingSettings {
				id
				enabled
				externalRecordingsChannel {
					id
					name
				}
				internalRecordingsChannel {
					id
					name
				}
			}
			meetingBriefSettings {
				id
				autoGenerationEnabled
				autoGenerationDelta
			}
			settings {
				googleMeetExtensionRecordingButtonEnabled
				voicePrintsEnabled
				customVocabularyList
				customVocabularyEnabled
			}
		}
	}
`;

type DefaultAutoRecordAndShareSettingsType = Pick<
	MeetingCopilotSettingsQuery["response"]["viewer"]["account"],
	| "defaultUserAutoRecordSettings"
	| "defaultUserMeetingRecapAutoShareSettings"
	| "billingAccount"
	| "allowUserAutoRecord"
>;

export const userAutoRecordSettingsMutation = graphql`
	mutation MeetingCopilotAutoRecordSettingsMutation(
		$autoRecordOwnership: FilterMeetingOwnership!
		$autoRecordEnabled: Boolean!
	) {
		updateUserAutoRecordSettings(meetingOwnershipFilter: $autoRecordOwnership, enabled: $autoRecordEnabled) {
			settings {
				id
				enabled
				meetingOwnershipFilter
			}
		}
	}
`;

export const userAutoShareSettingsMutation = graphql`
	mutation MeetingCopilotAutoShareSettingsMutation(
		$autoShareSendTo: UserMeetingRecapSendTo!
		$alwaysEmailWhenAttendee: Boolean!
	) {
		updateUserMeetingRecapAutoShareSettings(
			sendTo: $autoShareSendTo
			alwaysEmailWhenAttendee: $alwaysEmailWhenAttendee
		) {
			settings {
				id
				sendTo
				alwaysEmailWhenAttendee
			}
		}
	}
`;

const autoPublishMutation = graphql`
	mutation MeetingCopilotAutoPublishSettingsMutation(
		$enabled: Boolean!
		$externalRecordingsChannelId: ID
		$internalRecordingsChannelId: ID
	) @raw_response_type {
		updateUserRecordingAutoPublishSettings(
			enabled: $enabled
			externalRecordingsChannelId: $externalRecordingsChannelId
			internalRecordingsChannelId: $internalRecordingsChannelId
		) {
			settings {
				id
				enabled
				externalRecordingsChannel {
					id
					name
				}
				internalRecordingsChannel {
					id
					name
				}
			}
		}
	}
`;

function useUpdateMeetingBriefSettingsMutation(meetingBriefSettingsId: string) {
	const [commit, isInFlight] = useMutationPromise<MeetingCopilotUpdateMeetingBriefSettingsMutation>(graphql`
		mutation MeetingCopilotUpdateMeetingBriefSettingsMutation(
			$autoGenerationEnabled: Boolean
			$autoGenerationDelta: Int
		) @raw_response_type {
			updateUserMeetingBriefSettings(
				autoGenerationEnabled: $autoGenerationEnabled
				autoGenerationDelta: $autoGenerationDelta
			) {
				settings {
					id
					autoGenerationEnabled
					autoGenerationDelta
				}
			}
		}
	`);

	const updateMeetingBriefSettings = React.useCallback(
		(autoGenerationEnabled: boolean, autoGenerationDelta: number) => {
			commit({
				variables: { autoGenerationEnabled, autoGenerationDelta },
				optimisticResponse: {
					updateUserMeetingBriefSettings: {
						settings: {
							id: meetingBriefSettingsId,
							autoGenerationEnabled,
							autoGenerationDelta,
						},
					},
				},
			});
		},
		[commit, meetingBriefSettingsId],
	);

	return [updateMeetingBriefSettings, isInFlight] as const;
}

type Props = {
	initialAutoRecordSettings: UIAutoRecordSetting;
	initialAutoShareSettings: AttendeeValueOptions;
	initialGoogleMeetRecordSetting: boolean;
	initialVoicePrintsEnabledSetting: boolean;
	initialAlwaysEmailWhenAttendee: boolean;
	allowMeetingRecording: boolean;
	allowVoicePrints: boolean;
	lockAutoRecordSettingsToValue: boolean;
	lockAutoShareSendToSettingsToValue: boolean;
	lockAutoShareAlwaysEmailWhenAttendeeSettingsToValue: boolean;
	autoPublishRecordingSettings: MeetingCopilotSettingsQuery["response"]["viewer"]["autoPublishRecordingSettings"];
	meetingBriefSettings: MeetingCopilotSettingsQuery["response"]["viewer"]["meetingBriefSettings"];
	primaryAdmin: MeetingCopilotSettingsQuery["response"]["viewer"]["account"]["primaryAdmin"];
	isAccountAdmin: boolean;
	viewer: MeetingCopilotSettingsQuery["response"]["viewer"];
	refetch: () => void;
};

export enum MeetingOwnershipFilter {
	ALL_MEETINGS = "ALL_MEETINGS",
	ORGANIZED_BY_ME = "ORGANIZED_BY_ME",
	CUSTOM = "CUSTOM",
}

const mapUIAutoRecordSettingToMeetingOwnershipFilter = (setting: UIAutoRecordSetting): MeetingOwnershipFilter => {
	switch (setting) {
		case "all":
			return MeetingOwnershipFilter.ALL_MEETINGS;
		case "organized":
			return MeetingOwnershipFilter.ORGANIZED_BY_ME;
		case "custom":
			return MeetingOwnershipFilter.CUSTOM;
		default:
			return MeetingOwnershipFilter.CUSTOM;
	}
};

export const mapMeetingOwnershipFilterToUIAutoRecordSetting = (
	filter: "ALL_MEETINGS" | "ORGANIZED_BY_ME" | "CUSTOM",
): UIAutoRecordSetting => {
	switch (filter) {
		case MeetingOwnershipFilter.ALL_MEETINGS:
			return "all";
		case MeetingOwnershipFilter.ORGANIZED_BY_ME:
			return "organized";
		default:
			return "custom";
	}
};

const MEETING_BRIEF_DELTA_OPTIONS: Record<string, SelectorOption> = {
	"15": { value: "15", label: "15 minutes before" },
	"30": { value: "30", label: "30 minutes before" },
	"60": { value: "60", label: "1 hour before" },
	"120": { value: "120", label: "2 hours before" },
	"240": { value: "240", label: "4 hours before" },
	"1440": { value: "1440", label: "1 day before" },
	"2880": { value: "2880", label: "2 days before" },
	"4320": { value: "4320", label: "3 days before" },
};

function lockAutoShareSettingsToValue(
	account: DefaultAutoRecordAndShareSettingsType,
): [lockSendTo: boolean, lockAlwaysEmailWhenAttendee: boolean] {
	if (!account.billingAccount.hasAccessToDefaultUserConfigurations) {
		return [false, false];
	}
	const { defaultUserMeetingRecapAutoShareSettings } = account;
	let lockSendTo = false;
	let lockAlwaysEmailWhenAttendee = false;

	if (defaultUserMeetingRecapAutoShareSettings?.allowUsersToChangeSendTo === false) {
		lockSendTo = true;
	}
	if (defaultUserMeetingRecapAutoShareSettings?.allowUsersToChangeAlwaysEmailWhenAttendee === false) {
		lockAlwaysEmailWhenAttendee = true;
	}
	return [lockSendTo, lockAlwaysEmailWhenAttendee];
}

function lockAutoRecordSettingsToValue(account: DefaultAutoRecordAndShareSettingsType): boolean {
	/* only relevant for old design and will remove with the tooltip on hover on the selector when this setting is removed, in ENG-364:
	https://linear.app/fellow/issue/ENG-364/migrate-accounts-with-allow-user-auto-record=false-to-new-settings. */
	if (account.allowUserAutoRecord === false) {
		return true;
	}
	if (account.billingAccount.hasAccessToDefaultUserConfigurations) {
		return account.defaultUserAutoRecordSettings?.allowUsersToChangeAutoRecordSettings === false;
	}
	return false;
}

export function getLockDefaultAutoRecordAndShareSettings(
	account: DefaultAutoRecordAndShareSettingsType,
): [lockAutoRecord: boolean, lockSendTo: boolean, lockAlwaysEmailWhenAttendee: boolean] {
	const [lockSendTo, lockAlwaysEmailWhenAttendee] = lockAutoShareSettingsToValue(account);
	const lockAutoRecord = lockAutoRecordSettingsToValue(account);

	return [lockAutoRecord, lockSendTo, lockAlwaysEmailWhenAttendee];
}

function MeetingCopilot({
	initialAutoRecordSettings,
	initialAutoShareSettings,
	initialGoogleMeetRecordSetting,
	initialVoicePrintsEnabledSetting,
	initialAlwaysEmailWhenAttendee,
	allowMeetingRecording,
	allowVoicePrints,
	lockAutoRecordSettingsToValue,
	lockAutoShareSendToSettingsToValue,
	lockAutoShareAlwaysEmailWhenAttendeeSettingsToValue,
	autoPublishRecordingSettings,
	meetingBriefSettings,
	primaryAdmin,
	isAccountAdmin,
	refetch,
	viewer,
}: Props) {
	const meetingBriefsFlagOn = useFeatureIsOn("meeting-briefs");
	const customVocabularyFlagOn = useCustomVocabularyFeatureFlagOn();
	const hasAccessToMeetingBriefs =
		React.useContext(AppContext).account?.billingAccount.hasAccessToMeetingBriefs ?? false;

	const [uiAutoRecordSettings, setUIAutoRecordSettings] =
		React.useState<UIAutoRecordSetting>(initialAutoRecordSettings);
	const [uiAutoShareSettings, setUIAutoShareSettings] = React.useState<AttendeeValueOptions>(initialAutoShareSettings);
	const [alwaysEmailWhenAttendee, setAlwaysEmailWhenAttendee] = React.useState(initialAlwaysEmailWhenAttendee);

	const {
		isCustomVocabularyEnabled,
		customVocabularyList,
		isValidatingCustomVocabularyList,
		errorMessage: customVocabularyErrorMessage,
		onToggleCustomVocabularyEnabled,
		onDeleteCustomVocabularyWord,
		onAddCustomVocabularyWord,
		inputText: customVocabularyInputText,
		onInputChange: onCustomVocabularyInputChange,
	} = useCustomVocabularySetting(viewer);

	const history = useHistory();

	const relayEnv = useRelayEnvironment();

	const onUIAutoRecordSettingsChange = React.useCallback(
		(value: UIAutoRecordSetting) => {
			if (!["all", "organized", "off", "custom"].includes(value)) {
				return;
			}

			if (value === "custom") {
				dispatchAnalyticsTrack("[User Copilot Settings] Clicked radio custom auto-record rules");
			}

			setUIAutoRecordSettings(value);
			if (lockAutoRecordSettingsToValue) {
				return;
			}

			const enabled = value !== "off";
			const meetingOwnershipFilter = mapUIAutoRecordSettingToMeetingOwnershipFilter(value);

			commitMutationPromise<MeetingCopilotAutoRecordSettingsMutation>(relayEnv, {
				mutation: userAutoRecordSettingsMutation,
				variables: {
					autoRecordEnabled: enabled,
					autoRecordOwnership: meetingOwnershipFilter,
				},
			});
		},
		[lockAutoRecordSettingsToValue, relayEnv],
	);

	const updateAutoShareSettings = React.useCallback(
		(attendeeValue: AttendeeValueOptions, alwaysEmailWhenAttendeeValue: boolean) => {
			const autoShareSendTo =
				mapUIAttendeeValueToUserMeetingRecapSendTo[attendeeValue] ||
				mapUIAttendeeValueToUserMeetingRecapSendTo["internal-attendees"];

			commitMutationPromise<MeetingCopilotAutoShareSettingsMutation>(relayEnv, {
				mutation: userAutoShareSettingsMutation,
				variables: {
					autoShareSendTo,
					alwaysEmailWhenAttendee: alwaysEmailWhenAttendeeValue,
				},
			});
		},
		[relayEnv],
	);

	const onUIAutoShareSettingsChange = React.useCallback(
		(value: AttendeeValueOptions) => {
			if (!["all", "internal-attendees", "just-me", "none"].includes(value)) {
				return;
			}

			setUIAutoShareSettings(value);
			updateAutoShareSettings(value, alwaysEmailWhenAttendee);
		},
		[updateAutoShareSettings, alwaysEmailWhenAttendee],
	);

	const handleAlwaysEmailWhenAttendeeChange = React.useCallback(() => {
		setAlwaysEmailWhenAttendee(prev => !prev);
		updateAutoShareSettings(uiAutoShareSettings, !alwaysEmailWhenAttendee);
	}, [updateAutoShareSettings, uiAutoShareSettings, alwaysEmailWhenAttendee]);

	const [googleMeetExtensionRecordingButtonEnabled, setGoogleMeetExtensionRecordingButtonEnabled] = useUserSetting(
		"googleMeetExtensionRecordingButtonEnabled",
		initialGoogleMeetRecordSetting,
		{ updater: true },
	);

	const voicePrintFeatureEnabled = useFeatureIsOn("voiceprint");
	const [voicePrintsEnabled, setVoicePrintsEnabled] = useUserSetting(
		"voicePrintsEnabled",
		initialVoicePrintsEnabledSetting,
		{
			updater: React.useCallback((store, _data, newValue) => {
				const viewer = store.get("viewer");
				if (!viewer) return;
				viewer.setLinkedRecords([], "voicePrints");
				viewer.setValue(0, "voicePrintCount");
				const settings = viewer.getLinkedRecord("settings");
				settings?.setValue(newValue, "voicePrintsEnabled");
			}, []),
			onCompleted: React.useCallback((result, newValue) => {
				if (result.updateSettings && !newValue) {
					toast.success("All voice samples have been deleted.");
				}
			}, []),
		},
	);

	const onToggleVoicePrintsEnabled = React.useCallback(
		(newSetting: boolean) => {
			if (newSetting) {
				setVoicePrintsEnabled(newSetting);
				return;
			}

			warning(
				<>
					This action will permanently delete all voice samples we have collected. This action is irreversible. Are you
					sure you want to continue?
				</>,
				{
					title: "Delete all samples",
					yesText: "Delete",
				},
			).then(confirmed => {
				if (confirmed) setVoicePrintsEnabled(newSetting);
			});
		},
		[setVoicePrintsEnabled],
	);

	const autoPublishRecordingSettingsChange = React.useCallback(
		(enabled: boolean, externalRecordingsChannel: Channel | null, internalRecordingsChannel: Channel | null) => {
			commitMutationPromise<MeetingCopilotAutoPublishSettingsMutation>(relayEnv, {
				mutation: autoPublishMutation,
				variables: {
					enabled,
					externalRecordingsChannelId: externalRecordingsChannel?.id ?? null,
					internalRecordingsChannelId: internalRecordingsChannel?.id ?? null,
				},
				optimisticResponse: {
					updateUserRecordingAutoPublishSettings: {
						settings: {
							id: autoPublishRecordingSettings?.id ?? "",
							enabled,
							externalRecordingsChannel,
							internalRecordingsChannel,
						},
					},
				},
				// If we didn't have the setting before Relay won't be able to sync the new data
				// With what we got in the viewer, in that case we will have to update it manually
				updater: (store, payload) => {
					if (!payload) return;
					const viewer = store.get("viewer");
					if (!viewer || !payload.updateUserRecordingAutoPublishSettings?.settings) return;
					const autoPublishSettingsKey = "autoPublishRecordingSettings";
					const storedSettings = viewer.getLinkedRecord(autoPublishSettingsKey);
					if (storedSettings) return;
					const newSettings = store.get(payload.updateUserRecordingAutoPublishSettings.settings.id);
					if (!newSettings) return;
					viewer.setLinkedRecord(newSettings, autoPublishSettingsKey);
				},
			});
		},
		[relayEnv, autoPublishRecordingSettings],
	);

	const autoPublishRecordingToggle = React.useCallback(
		(enabled: boolean) => {
			autoPublishRecordingSettingsChange(
				enabled,
				autoPublishRecordingSettings?.externalRecordingsChannel ?? null,
				autoPublishRecordingSettings?.internalRecordingsChannel ?? null,
			);
		},
		[autoPublishRecordingSettingsChange, autoPublishRecordingSettings],
	);

	const onExternalRecordingsAutoPublishChannelChange = React.useCallback(
		(channel: Channel | null) => {
			autoPublishRecordingSettingsChange(
				true,
				channel,
				autoPublishRecordingSettings?.internalRecordingsChannel ?? null,
			);
		},
		[autoPublishRecordingSettingsChange, autoPublishRecordingSettings],
	);

	const onInternalRecordingsAutoPublishChannelChange = React.useCallback(
		(channel: Channel) => {
			autoPublishRecordingSettingsChange(
				true,
				autoPublishRecordingSettings?.externalRecordingsChannel
					? autoPublishRecordingSettings.externalRecordingsChannel
					: null,
				channel,
			);
		},
		[autoPublishRecordingSettingsChange, autoPublishRecordingSettings],
	);

	const [page, setPage] = React.useState<"custom rules" | "voice prints" | undefined>();

	const onNavigateConfigureCustomRules = React.useCallback(() => {
		dispatchAnalyticsTrack("[User Copilot Settings] Clicked configure custom auto-record rules");
		setPage("custom rules");
	}, []);

	const onNavigateToVoicePrintsPage = React.useCallback(() => {
		dispatchAnalyticsTrack("[User Copilot Settings] Clicked view and configure voice prints");
		setPage("voice prints");
	}, []);

	const onNavigateToCopilotSettings = React.useCallback(() => {
		setPage(undefined);
	}, []);

	const [updateMeetingBriefSettings] = useUpdateMeetingBriefSettingsMutation(meetingBriefSettings.id);
	const setMeetingBriefsEnabled = React.useCallback(
		(enabled: boolean) => {
			updateMeetingBriefSettings(enabled, meetingBriefSettings.autoGenerationDelta);
		},
		[updateMeetingBriefSettings, meetingBriefSettings],
	);

	const meetingBriefDelta =
		MEETING_BRIEF_DELTA_OPTIONS[meetingBriefSettings.autoGenerationDelta.toString()] ??
		MEETING_BRIEF_DELTA_OPTIONS["15"];

	const onMeetingBriefDeltaChange = React.useCallback(
		(option: SelectorOption | null) => {
			if (!option) return;
			updateMeetingBriefSettings(true, parseInt(option.value, 10));
		},
		[updateMeetingBriefSettings],
	);

	const onNavigateToCustomVocabularyPage = React.useCallback(() => {
		history.push("/account/copilot/settings#custom_vocabulary");
	}, [history]);

	if (page === "custom rules") {
		return (
			<CustomRulesSettings
				navigateBack={onNavigateToCopilotSettings}
				viewer={viewer}
				refetch={refetch}
				showCustomRules={uiAutoRecordSettings === "custom"}
				hasAdminAccess={isAccountAdmin}
			/>
		);
	} else if (page === "voice prints") {
		return <VoicePrints navigateBack={onNavigateToCopilotSettings} />;
	}

	return (
		<div className={styles.main}>
			<SettingsPageTitle>Note Taker</SettingsPageTitle>
			{allowMeetingRecording && (
				<SettingsPageSubtitle>
					<div className={styles.subtitle}>
						Manage your meeting recording and summarization preferences.
						<HelpCenterLink articleId="7995695">Learn more.</HelpCenterLink>
					</div>
				</SettingsPageSubtitle>
			)}
			<>
				{!allowMeetingRecording ? (
					<InfoBanner>
						Note Taker <b>is not enabled</b> for your workspace.{" "}
						{isAccountAdmin ? (
							<>
								You can enable it in <Link to="/account/settings/">workspace settings</Link>.
							</>
						) : primaryAdmin ? (
							<>
								{/* eslint-disable-next-line max-len */}
								Contact your workspace administrator{" "}
								<a href={`mailto:${primaryAdmin.email}`}>{primaryAdmin.fullName}</a> to start using your Fellow Note
								Taker.
							</>
						) : (
							<>Contact your workspace administrator to start using your Note Taker.</>
						)}
					</InfoBanner>
				) : (
					<>
						<Divider />
						<Row>
							<Row.Left>
								<Label>Fellow will join and take AI meeting notes for:</Label>
								<SubLabel></SubLabel>
							</Row.Left>
						</Row>
						<AutoRecordingSettingsSelector
							value={uiAutoRecordSettings}
							onChange={onUIAutoRecordSettingsChange}
							className={styles.aiSettingSelector}
							includeCustomOption
							lockToValue={lockAutoRecordSettingsToValue}
						/>
						<CustomRulesBanner
							viewer={viewer}
							onNavigateConfigure={onNavigateConfigureCustomRules}
							showCustomRules={uiAutoRecordSettings === "custom"}
						/>
						<Divider />
						<Row>
							<Row.Left>
								<Label>
									Email AI meeting notes to:&nbsp;
									<Tooltip
										maxWidth={140}
										content="This will be the default setting for meetings you organize"
										className={styles.centeredTooltip}
									>
										<FontAwesomeIcon icon={faInfoCircle} />
									</Tooltip>
								</Label>
								<SubLabel></SubLabel>
							</Row.Left>
						</Row>
						<RecapSettings
							value={uiAutoShareSettings}
							onChange={onUIAutoShareSettingsChange}
							alwaysEmailWhenAttendee={alwaysEmailWhenAttendee}
							onAlwaysEmailWhenAttendeeChange={handleAlwaysEmailWhenAttendeeChange}
							emailDomainList={viewer.account.emailDomainList}
							selectorWidth="50%"
							lockToSendToValue={lockAutoShareSendToSettingsToValue}
							lockAlwaysEmailWhenAttendee={lockAutoShareAlwaysEmailWhenAttendeeSettingsToValue}
						/>
						{voicePrintFeatureEnabled && (
							<>
								<Divider />
								<Row>
									<Row.Left>
										<Label>Voice detection</Label>
										<SubLabel>Automatically assign my name to transcript segments that match my voice</SubLabel>
									</Row.Left>
									<Row.Right>
										<Tooltip
											disabled={allowVoicePrints}
											content="Voice detection is disabled by your workspace administrator"
										>
											<div>
												<Switch
													size="small"
													checked={(voicePrintsEnabled ?? false) && allowVoicePrints}
													onCheckedChange={onToggleVoicePrintsEnabled}
													disabled={!allowVoicePrints}
												/>
											</div>
										</Tooltip>
									</Row.Right>
								</Row>
								{voicePrintsEnabled && allowVoicePrints && (
									<div className={styles.voicePrintsBanner}>
										{viewer.voicePrintCount === 0 ? (
											<p className={styles.noVoicePrintsText}>
												Check back after a few meetings to explore the audio samples we&apos;ve gathered.
											</p>
										) : (
											<p>
												<strong className={styles.voicePrintCountText}>
													{viewer.voicePrintCount} audio sample{viewer.voicePrintCount === 1 ? "" : "s"}
												</strong>{" "}
												have been collected for your voice.{" "}
												<ButtonLink onClick={onNavigateToVoicePrintsPage}>
													View and configure <FontAwesomeIcon icon={faArrowRight} />
												</ButtonLink>
											</p>
										)}
									</div>
								)}
							</>
						)}
						<Divider />
						<Row>
							<Row.Left>
								<Label>
									Show quick access <b>Take AI Notes</b> button on Google Meet
								</Label>
								<SubLabel></SubLabel>
							</Row.Left>
							<Row.Right>
								<Switch
									size="small"
									checked={googleMeetExtensionRecordingButtonEnabled || false}
									onCheckedChange={setGoogleMeetExtensionRecordingButtonEnabled}
								/>
							</Row.Right>
						</Row>
						<>
							<Divider />
							<Row>
								<Row.Left>
									<Label>Auto-share recaps to a channel</Label>
									<SubLabel>After a meeting, automatically share the Recap to a selected Fellow channel</SubLabel>
								</Row.Left>
								<Row.Right>
									<Switch
										size="small"
										checked={autoPublishRecordingSettings?.enabled}
										onCheckedChange={autoPublishRecordingToggle}
									/>
								</Row.Right>
							</Row>
							{autoPublishRecordingSettings?.enabled && (
								<div className={styles.autoPublishSettings}>
									<div className={styles.autoPublishSetting}>
										<label>
											Set all <strong>external recordings</strong> to auto-publish to the following channel:
										</label>
										<ChannelSelector
											className={styles.autoPublishChannelSelector}
											selected={autoPublishRecordingSettings?.externalRecordingsChannel}
											onChange={onExternalRecordingsAutoPublishChannelChange}
											memberOnly
										/>
									</div>
									<div className={styles.autoPublishSetting}>
										<label>
											Set all <strong>internal recordings</strong> to auto-publish to the following channel:
										</label>
										<ChannelSelector
											className={styles.autoPublishChannelSelector}
											selected={autoPublishRecordingSettings?.internalRecordingsChannel}
											onChange={onInternalRecordingsAutoPublishChannelChange}
											memberOnly
										/>
									</div>
								</div>
							)}
						</>
						{meetingBriefsFlagOn && hasAccessToMeetingBriefs && (
							<>
								<Divider />
								<Row>
									<Row.Left>
										<Label>Pre-meeting brief</Label>
										<SubLabel>Automatically get briefed before an upcoming meeting</SubLabel>
									</Row.Left>
									<Row.Right>
										<Switch
											size="small"
											checked={meetingBriefSettings.autoGenerationEnabled}
											onCheckedChange={setMeetingBriefsEnabled}
										/>
									</Row.Right>
								</Row>
								{meetingBriefSettings.autoGenerationEnabled && (
									<div className={styles.autoPublishSettings}>
										<div className={styles.autoPublishSetting}>
											<label>Brief me when a meeting is about to start:</label>
											<Selector
												className={styles.meetingBriefDeltaSelector}
												components={{ Control: MeetingBriefDeltaSelectorControl }}
												condensed
												menuPlacement="auto"
												value={meetingBriefDelta}
												options={Object.values(MEETING_BRIEF_DELTA_OPTIONS)}
												onChange={onMeetingBriefDeltaChange}
											/>
										</div>
									</div>
								)}
							</>
						)}
						{customVocabularyFlagOn && (
							<>
								<Divider />
								<Row>
									<Row.Left>
										<Label>Custom vocabulary for transcription</Label>
										<SubLabel>
											Reduce transcription errors by adding custom English words to improve accuracy. Your company name
											and attendee names are already known.
										</SubLabel>
									</Row.Left>
									<BillingIdentity feature="custom_vocabulary" viewer={viewer}>
										{(extraProps, icon, paywalled) => (
											<Row.Right {...extraProps}>
												{icon}
												{paywalled && <>&emsp;</>}
												<Switch
													size="small"
													name="control_custom_vocabulary"
													checked={paywalled ? false : (isCustomVocabularyEnabled ?? false)}
													onCheckedChange={paywalled ? undefined : onToggleCustomVocabularyEnabled}
												/>
											</Row.Right>
										)}
									</BillingIdentity>
								</Row>
								{isCustomVocabularyEnabled && (
									<div className={cx(settingsStyles.customVocabularyField)}>
										<form
											id="custom-vocabulary-form"
											className={settingsStyles.customVocabularyFieldInputContainer}
											onSubmit={onAddCustomVocabularyWord}
										>
											<Input
												id="custom-vocabulary-input"
												value={customVocabularyInputText}
												onChange={onCustomVocabularyInputChange}
												isInvalid={Boolean(customVocabularyErrorMessage)}
												className={settingsStyles.customVocabularyFieldInput}
												placeholder="Add words or phrases, separated by commas…"
												name="custom_vocabulary_input"
											/>
											<Button
												type="submit"
												variant="primary"
												size="slim"
												disabled={!customVocabularyInputText || isValidatingCustomVocabularyList}
											>
												Add
											</Button>
										</form>
										{customVocabularyErrorMessage && (
											<div className={settingsStyles.customVocabularyErrorMessage}>{customVocabularyErrorMessage}</div>
										)}

										{customVocabularyList && customVocabularyList.length > 0 && (
											<div className={settingsStyles.customVocabularyFieldChips}>
												{customVocabularyList.map(word => (
													<TextChip text={word} onDelete={onDeleteCustomVocabularyWord} key={word} />
												))}
											</div>
										)}
										{viewer.account.customVocabularyEnabled && (
											<div className={styles.customVocabularyBanner}>
												{viewer.account.customVocabularyList.length > 0 ? (
													<>
														<div>
															The following words have been added to your workspace’s custom vocabulary:
															<div>{viewer.account.customVocabularyList.join(", ")}</div>
														</div>
														{viewer.user.isAccountAdmin && (
															<ButtonLink onClick={onNavigateToCustomVocabularyPage}>Edit</ButtonLink>
														)}
													</>
												) : (
													viewer.user.isAccountAdmin && (
														<>
															<div>Want to add custom vocabulary for everyone in your workspace?</div>
															<ButtonLink onClick={onNavigateToCustomVocabularyPage}>Add to workspace</ButtonLink>
														</>
													)
												)}
											</div>
										)}
									</div>
								)}
							</>
						)}
					</>
				)}
			</>
		</div>
	);
}

// This is only here to change the background colour...
function MeetingBriefDeltaSelectorControl(props: ControlProps<SelectorOption, false>) {
	return (
		<reactSelectComponents.Control
			{...props}
			className={cx(styles.meetingBriefDeltaSelectorControl, {
				[styles.open]: props.selectProps.menuIsOpen,
			})}
		/>
	);
}

function MeetingCopilotWithData() {
	const [fetchKey, setFetchKey] = React.useState(0);

	const data = useLazyLoadQuery<MeetingCopilotSettingsQuery>(query, {}, { fetchPolicy: "store-and-network", fetchKey });
	const autoShareSettings = React.useMemo(() => {
		if (!data.viewer.meetingRecapAutoShareSettings) return "internal-attendees";
		switch (data.viewer.meetingRecapAutoShareSettings?.sendTo) {
			case "ALL":
				// All will behave like internal attendees when public links are off
				// We don't want to actually change the setting on the backend because if public links
				// Are toggled back on we want users to keep their settings
				return data.viewer.account.allowPublicRecap ? "all" : "internal-attendees";
			case "INTERNAL":
				return "internal-attendees";
			case "JUST_ME":
				return "just-me";
			case "NONE":
				return "none";
			default:
				return "internal-attendees";
		}
	}, [data.viewer.meetingRecapAutoShareSettings, data.viewer.account.allowPublicRecap]);

	const googleMeetRecordingButtonEnabled = data.viewer.settings.googleMeetExtensionRecordingButtonEnabled;
	const alwaysEmailWhenAttendee = data.viewer.meetingRecapAutoShareSettings?.alwaysEmailWhenAttendee ?? false;
	const refetch = React.useCallback(() => setFetchKey(key => key + 1), []);

	const [
		lockAutoRecordSettingsToValue,
		lockAutoShareSendToSettingsToValue,
		lockAutoShareAlwaysEmailWhenAttendeeSettingsToValue,
	] = React.useMemo(() => {
		return getLockDefaultAutoRecordAndShareSettings(data.viewer.account);
	}, [data.viewer.account]);

	const autoRecordSettings = React.useMemo(() => {
		// if the user has no auto record settings, and account setting is locked, use the account's default user auto record setting
		if (!data.viewer.autoRecordSettings && lockAutoRecordSettingsToValue) {
			if (!data.viewer.account.defaultUserAutoRecordSettings.enabled) {
				return "off";
			} else {
				return mapMeetingOwnershipFilterToUIAutoRecordSetting(
					data.viewer.account.defaultUserAutoRecordSettings.meetingOwnershipFilter,
				);
			}
		}
		if (!data.viewer.autoRecordSettings?.enabled) return "off";
		return mapMeetingOwnershipFilterToUIAutoRecordSetting(data.viewer.autoRecordSettings.meetingOwnershipFilter);
	}, [
		data.viewer.account.defaultUserAutoRecordSettings,
		data.viewer.autoRecordSettings,
		lockAutoRecordSettingsToValue,
	]);

	return (
		<MeetingCopilot
			initialAlwaysEmailWhenAttendee={alwaysEmailWhenAttendee}
			initialAutoRecordSettings={autoRecordSettings}
			initialAutoShareSettings={autoShareSettings}
			initialGoogleMeetRecordSetting={googleMeetRecordingButtonEnabled}
			initialVoicePrintsEnabledSetting={data.viewer.settings.voicePrintsEnabled}
			allowMeetingRecording={data.viewer.hasAccessToCopilot}
			allowVoicePrints={data.viewer.account.allowVoicePrints}
			lockAutoRecordSettingsToValue={lockAutoRecordSettingsToValue}
			lockAutoShareSendToSettingsToValue={lockAutoShareSendToSettingsToValue}
			lockAutoShareAlwaysEmailWhenAttendeeSettingsToValue={lockAutoShareAlwaysEmailWhenAttendeeSettingsToValue}
			autoPublishRecordingSettings={data.viewer.autoPublishRecordingSettings}
			meetingBriefSettings={data.viewer.meetingBriefSettings}
			primaryAdmin={data.viewer.account.primaryAdmin}
			isAccountAdmin={data.viewer.user.isAccountAdmin}
			refetch={refetch}
			viewer={data.viewer}
		/>
	);
}

export default MeetingCopilotWithData;
